import React, { useEffect, useState } from "react";

const GasCylinderLoader = ({ 
  size = "md",
  showProgress = true, 
  autoProgress = true,
  progress: externalProgress,
  onComplete,
}) => {
  const [progress, setProgress] = useState(0);

  const sizeConfig = {
    sm: { cylinderW: 30, cylinderH: 45, barWidth: 200, containerH: 120 },
    md: { cylinderW: 40, cylinderH: 60, barWidth: 300, containerH: 150 },
    lg: { cylinderW: 50, cylinderH: 75, barWidth: 400, containerH: 180 },
  };

  const config = sizeConfig[size] || sizeConfig.md;

  useEffect(() => {
    if (autoProgress && externalProgress === undefined) {
      const interval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 100) {
            if (onComplete) onComplete();
            return 0;
          }
          return prev + 1;
        });
      }, 30);
      return () => clearInterval(interval);
    }
  }, [autoProgress, externalProgress, onComplete]);

  const currentProgress = externalProgress !== undefined ? externalProgress : progress;

  // Calculate cylinder position based on progress
  const cylinderPosition = (currentProgress / 100) * (config.barWidth - config.cylinderW);

  return (
    <div style={{
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '60px 20px 40px',
      background: 'transparent',
      minHeight: `${config.containerH + 40}px`,
    }}>
      
      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-8px); }
          }
          @keyframes glow {
            0%, 100% { 
              box-shadow: 0 4px 12px rgba(255, 107, 107, 0.3),
                          0 0 20px rgba(255, 107, 107, 0.2);
            }
            50% { 
              box-shadow: 0 6px 16px rgba(255, 107, 107, 0.5),
                          0 0 30px rgba(255, 107, 107, 0.3);
            }
          }
          @keyframes shimmer {
            0% { left: -50%; }
            100% { left: 150%; }
          }
          @keyframes flame {
            0%, 100% { transform: scale(1) translateY(0); opacity: 1; }
            50% { transform: scale(1.1) translateY(-2px); opacity: 0.8; }
          }
        `}
      </style>

      {/* Progress Bar Container */}
      <div style={{
        position: 'relative',
        width: `${config.barWidth}px`,
        height: '10px',
        background: '#e9ecef',
        borderRadius: '20px',
        overflow: 'visible',
        boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.06)',
        marginTop: '8px',
      }}>
        
        {/* Progress Fill */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          height: '100%',
          width: `${currentProgress}%`,
          background: 'linear-gradient(90deg, #ff6b6b 0%, #ff8787 50%, #ffa07a 100%)',
          borderRadius: '20px',
          transition: 'width 0.3s ease-out',
          boxShadow: '0 0 15px rgba(255, 107, 107, 0.4)',
          overflow: 'hidden',
        }}>
          {/* Shimmer effect */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: '-50%',
            width: '50%',
            height: '100%',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
            animation: 'shimmer 2s infinite',
          }} />
        </div>

        {/* Floating Cylinder */}
        <div style={{
          position: 'absolute',
          bottom: '100%',
          left: `${cylinderPosition}px`,
          transform: 'translateY(-16px)',
          transition: 'left 0.3s ease-out',
          animation: 'float 2s ease-in-out infinite',
          zIndex: 10,
        }}>
          
          {/* Top Flame */}
          <div style={{
            position: 'absolute',
            top: `-${config.cylinderH * 0.3}px`,
            left: '50%',
            transform: 'translateX(-50%)',
            animation: 'flame 0.8s ease-in-out infinite',
            filter: 'drop-shadow(0 0 8px rgba(255, 152, 0, 0.6))',
          }}>
            <svg width={config.cylinderW * 0.6} height={config.cylinderH * 0.4} viewBox="0 0 16 20">
              <path 
                d="M8 2C8 2 5 6 5 10C5 12.5 6.5 14 8 14C9.5 14 11 12.5 11 10C11 6 8 2 8 2Z" 
                fill="url(#flameGrad)"
              />
              <defs>
                <linearGradient id="flameGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#ffa500" />
                  <stop offset="100%" stopColor="#ff6347" />
                </linearGradient>
              </defs>
            </svg>
          </div>

          {/* Cylinder Body */}
          <div style={{
            position: 'relative',
            width: `${config.cylinderW}px`,
            height: `${config.cylinderH}px`,
          }}>
            
            {/* Main Cylinder Container */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '100%',
              height: '100%',
            }}>
              
              {/* Cylinder Body */}
              <div style={{
                position: 'relative',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(135deg, #ff4757 0%, #ff6b6b 50%, #ff4757 100%)',
                borderRadius: '8px',
                overflow: 'hidden',
                animation: 'glow 2s ease-in-out infinite',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 4px 12px rgba(255, 107, 107, 0.2)',
              }}>
                
                {/* Subtle Highlight */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: '10%',
                  width: '20%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
                  filter: 'blur(2px)',
                  borderRadius: '6px',
                }} />

                {/* Animated Shimmer */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)',
                  backgroundSize: '200% 100%',
                  animation: 'shimmer 2s infinite',
                  opacity: 0.4,
                }} />

                {/* Gas Level Fill */}
                <div style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  width: '100%',
                  height: `${currentProgress}%`,
                  background: 'linear-gradient(180deg, rgba(255, 165, 0, 0.4) 0%, rgba(255, 140, 0, 0.6) 100%)',
                  borderRadius: '0 0 6px 6px',
                  transition: 'height 0.3s ease-out',
                  overflow: 'hidden',
                }}>
                  {/* Wave effect */}
                  <div style={{
                    position: 'absolute',
                    top: '-8px',
                    left: '-50%',
                    width: '200%',
                    height: '12px',
                    background: 'radial-gradient(ellipse at center, rgba(255,255,255,0.4) 0%, transparent 70%)',
                    borderRadius: '50%',
                    animation: 'shimmer 2s ease-in-out infinite',
                  }} />
                </div>

                {/* Warning Icon */}
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: `${config.cylinderW * 0.75}px`,
                  height: `${config.cylinderW * 0.75}px`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                }}>
                  <svg width="100%" height="100%" viewBox="0 0 24 24">
                    <path 
                      d="M12 2L1 21h22L12 2zm0 4l8 14H4l8-14z" 
                      fill="rgba(255, 255, 255, 0.9)"
                    />
                    <path 
                      d="M12 8v6" 
                      stroke="rgba(255, 255, 255, 0.9)" 
                      strokeWidth="2" 
                      strokeLinecap="round"
                    />
                    <circle cx="12" cy="17" r="1" fill="rgba(255, 255, 255, 0.9)"/>
                  </svg>
                  
                  {/* Minimal Flame */}
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    animation: 'flame 0.8s ease-in-out infinite',
                  }}>
                    <svg width="12" height="12" viewBox="0 0 16 16">
                      <path 
                        d="M8 2C8 2 6 5 6 8C6 9.5 7 10 8 10C9 10 10 9.5 10 8C10 5 8 2 8 2Z" 
                        fill="rgba(255, 255, 255, 0.9)"
                      />
                    </svg>
                  </div>
                </div>

                {/* GAS Text */}
                <div style={{
                  position: 'absolute',
                  bottom: '8px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  color: 'white',
                  fontSize: `${config.cylinderW * 0.3}px`,
                  fontWeight: '800',
                  letterSpacing: '1.5px',
                  zIndex: 10,
                  textShadow: '0 1px 4px rgba(0,0,0,0.3)',
                  fontFamily: 'system-ui, sans-serif',
                  opacity: 0.9,
                }}>
                  GAS
                </div>

                {/* Minimal Band */}
                <div style={{
                  position: 'absolute',
                  bottom: `${config.cylinderH * 0.35}px`,
                  left: 0,
                  width: '100%',
                  height: '1.5px',
                  background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.5) 50%, transparent 100%)',
                }} />
              </div>

              {/* Cylinder Top */}
              <div style={{
                position: 'absolute',
                top: `-${config.cylinderH * 0.15}px`,
                left: '50%',
                transform: 'translateX(-50%)',
                width: `${config.cylinderW * 0.65}px`,
                height: `${config.cylinderH * 0.2}px`,
                background: 'linear-gradient(135deg, #ff5252 0%, #ff6b6b 100%)',
                borderRadius: '6px 6px 0 0',
                boxShadow: '0 -4px 12px rgba(255, 82, 82, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
              }}>
                {/* Side Handles */}
                <div style={{
                  position: 'absolute',
                  top: '40%',
                  left: `-${config.cylinderW * 0.12}px`,
                  width: `${config.cylinderW * 0.28}px`,
                  height: `${config.cylinderH * 0.1}px`,
                  background: 'linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%)',
                  borderRadius: '3px',
                  boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1), 1px 1px 3px rgba(0,0,0,0.1)',
                  border: '1px solid rgba(0,0,0,0.05)',
                }} />
                <div style={{
                  position: 'absolute',
                  top: '40%',
                  right: `-${config.cylinderW * 0.12}px`,
                  width: `${config.cylinderW * 0.28}px`,
                  height: `${config.cylinderH * 0.1}px`,
                  background: 'linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%)',
                  borderRadius: '3px',
                  boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1), -1px 1px 3px rgba(0,0,0,0.1)',
                  border: '1px solid rgba(0,0,0,0.05)',
                }} />
                
                {/* Valve */}
                <div style={{
                  position: 'absolute',
                  top: `-${config.cylinderH * 0.12}px`,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: `${config.cylinderW * 0.25}px`,
                  height: `${config.cylinderH * 0.15}px`,
                  background: 'linear-gradient(135deg, #666 0%, #444 100%)',
                  borderRadius: '2px 2px 0 0',
                  boxShadow: '0 -2px 6px rgba(0,0,0,0.2)',
                }}>
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '70%',
                    height: '1px',
                    background: '#777',
                    borderRadius: '1px',
                  }} />
                </div>
              </div>

              {/* Bottom Feet */}
              {[25, 50, 75].map((left, i) => (
                <div
                  key={i}
                  style={{
                    position: 'absolute',
                    bottom: `-${config.cylinderH * 0.08}px`,
                    left: `${left}%`,
                    transform: 'translateX(-50%)',
                    width: `${config.cylinderW * 0.2}px`,
                    height: `${config.cylinderW * 0.2}px`,
                    background: 'linear-gradient(135deg, #555 0%, #333 100%)',
                    borderRadius: '2px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  }}
                />
              ))}
            </div>

            {/* Top Flame */}
            <div style={{
              position: 'absolute',
              top: `-${config.cylinderH * 0.3}px`,
              left: '50%',
              transform: 'translateX(-50%)',
              animation: 'flame 0.6s ease-in-out infinite',
              filter: 'drop-shadow(0 0 6px rgba(255, 140, 0, 0.6))',
            }}>
              <svg width={config.cylinderW * 0.6} height={config.cylinderH * 0.4} viewBox="0 0 16 16">
                <path 
                  d="M8 2C8 2 5 6 5 9C5 11 7 12 8 12C9 12 11 11 11 9C11 6 8 2 8 2Z" 
                  fill="rgba(255, 165, 0, 0.9)"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Text */}
      {showProgress && (
        <div style={{
          marginTop: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}>
          <span style={{
            fontSize: '13px',
            fontWeight: '500',
            color: '#868e96',
            letterSpacing: '0.3px',
          }}>
            Loading
          </span>
          <span style={{
            fontSize: '18px',
            fontWeight: '700',
            color: '#ff6b6b',
            minWidth: '50px',
            textAlign: 'left',
          }}>
            {Math.round(currentProgress)}%
          </span>
        </div>
      )}
    </div>
  );
};

export default GasCylinderLoader;